import React, { useContext, useReducer,useState } from "react";
import { resetTabName, tab, tabName } from "./BrandScore/BrandScore";
import { LLM_OPTIONS, OVERVIEW_LLM_LABELS } from "../../utils/constant";
import { ENTITY_LABELS } from "../../utils/labelMapping";

const sanitizeKey = (key) => {
  return key.replace(/[.#$\/\[\]]/g, "_");
};

const updateScores = (
  scores,
  llmList,
  categories,
  markets = [],
  personas = []
) => {
  if (typeof markets === "string") markets = [markets];
  if (!Array.isArray(markets)) markets = [];

  if (typeof llmList === "string") llmList = [llmList];
  if (!Array.isArray(llmList)) llmList = [];
const personaTitles = personas
  .map(p => {
    if (typeof p === "object" && p?.title) return p.title;
    if (typeof p === "string") return p;
    return null;
  })
  .filter(Boolean);

  llmList.forEach(llm => {
    if (!scores[llm]) scores[llm] = {};

    categories.forEach(cat => {
      
      // CASE 1: No markets → category = []
      if (!markets.length) {
        if (!scores[llm][cat]) scores[llm][cat] = [];
        return;
      }

      // CASE 2: Markets exist → category = { market: [] }
      if (!scores[llm][cat]) scores[llm][cat] = {};

      markets.forEach(market => {
        
        // If no personas → final level = []
        if (!personas.length) {
          if (!scores[llm][cat][market]) scores[llm][cat][market] = [];
          return;
        }

        // CASE 3: Markets + Personas → final level = []
        if (!scores[llm][cat][market]) scores[llm][cat][market] = {};

        personaTitles.forEach(persona => {
          if (!scores[llm][cat][market][persona]) {
            scores[llm][cat][market][persona] = [];
          }
        });
      });
    });
  });

  return { ...scores };
};

const TASK_STATUS = {
  SUCCESS: "success",
  ERROR: "error",
  PENDING: "pending",
};

const TASKS = {
  BRAND_DESCRIPTION: "branddescription",
  BRAND_THEMES: "brandthemes",
  BRAND_SNIPPETS: "brandsnippets",
  BRAND_SCORE: "brandscore",
  RANK_SCORE: "rankscore",
  CATEGORY_DIMENSION: "categorydimensions",
  CORE_ATTRIBUTES: "coreattributes",
  COMPETITORS: "competitors",
  ATTRIBUTES: "attributes",
};

const sanitizeObject = (obj) => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(sanitizeObject);
  }
  return Object.keys(obj).reduce((result, key) => {
    const sanitizedKey = sanitizeKey(key);
    result[sanitizedKey] = sanitizeObject(obj[key]);
    return result;
  }, {});
};

function setNestedValue(obj, keys, value) {
  const lastKey = keys.pop();
  const nested = keys.reduce((acc, key) => {
    if (!acc[key]) acc[key] = {};
    return acc[key];
  }, obj);
  nested[lastKey] = value;
  return obj;
}


const resolveSelectionRules = (state, nextPersona, nextMarket, nextCategory) => {
  const personaSettings = state.personaSettings || {};
  const applyAllMarkets = personaSettings.applyPersonaToAllMarkets;
  const applyAllCategory = personaSettings.applyPersonaToAllCategory;

  const primaryPersona = state.persona?.[0]?.title ?? state.persona?.[0];
  const primaryMarket = state.markets?.[0];
  const primaryCategory = state.category?.[0];

  const requestedPersona = nextPersona ?? state.selectedPersona;

  let persona = requestedPersona;
  let market = nextMarket ?? state.selectedMarket;
  let category = nextCategory ?? state.selectedCategory;

  let isCombinationAllowed = true;

  // CASE 4
  if (applyAllCategory && applyAllMarkets) {
    return { persona, market, category, isCombinationAllowed };
  }

  // CASE 1
  if (!applyAllCategory && !applyAllMarkets) {
    if (category !== primaryCategory || market !== primaryMarket) {
      persona = primaryPersona;
    }
  }

  // CASE 2
  if (!applyAllCategory && applyAllMarkets) {
    if (category !== primaryCategory) {
      persona = primaryPersona;
    }
  }

  // CASE 3
  if (applyAllCategory && !applyAllMarkets) {
    if (market !== primaryMarket) {
      persona = primaryPersona;
    }
  }

  // 🔔 works for persona / market / category change
  if (persona !== requestedPersona) {
    isCombinationAllowed = false;
  }

  return { persona, market, category, isCombinationAllowed };
};



const uniqueArray = (array) => {
  return array.filter(
    (obj, index, self) => index === self.findIndex((o) => o?.name === obj?.name)
  );
};
const updateCount = (totalCount, completedTaskCount) => {
  return {
    totalCount,
    completedTaskCount,
  };
};

const INITIAL_STATE = {
  name: "",
  entity: "",
  category: [],
  market: "",
  markets: [],
  persona: [],
  llm: ["gpt-5.1"],
  primary_llm: "gpt-5.1",
  type: "brand",
  domain: "",
  transactionStatus: "",
  sourceValidationStatus: "PENDING",
  finalTransactionStatus: "INITIATED",
  loading: false,
  competitors: [],
  brand_scores: {},
  category_dimensions: {},
  core_attributes: {},
  sources: {},
  brand_description: {},
  brand_snippets: {},
  brand_themes: [],
  positive_negative_attributes: {},
  top_products: [],
  taxonomies: {},
  is_launched: false,
  abort_launch: false,
  formData: false,
  isCombinationAllowed:true,
  is_resetFormState: false,
  location: "United States",
  occupation: "",
  attributes: [],
  category_taxonomies: [],
  product_prefs: [],
  pre_category_dimensions: [],
  selectedCompetitor: 0,
  rank_scores: {},
  showReportCheckbox: false,
  showAnimation: true,
  selectAll: false,
  selectedSection: {},
  status: {},
  transactionHistory: [],
  launchRecord: [],
  conversations: {},
  contentMapping: {},
  rbiType: "propensityToRecommend",
  keywords: [],
  auditLimits: null,
  conversationSettings:{
    selectedSites: [],
    excludeBrandOwnedSources: false,
  },
};

export const DiscoveryContext = React.createContext({
  location: "",
  domain: "",
  occupation: "",
  attributes: [],
  product_prefs: [],
  state: {},
  status: {},
  category: [],
  market: "",
  markets: [],
  llm: [],
  persona: [],
  type: "",
  loading: false,
  formData: false,
  is_resetFormState: false,
  conversationEnabled: false,
  personaSettings:{
    applyPersonaToAllCategory:false,
    applyPersonaToAllMarkets:false
  },
  updateId: () => { },
  updateLoading: () => { },
  brand_scores: {},
  category_taxonomies: [],
  updateCategoryTaxonomies: () => { },
  updateBrandScores: () => { },
  sources: [],
  updateSources: () => { },
  core_attributes: [],
  updateCoreAttributes: () => { },
  competitors: [],
  updatedCompetitors: () => { },
  category_dimensions: {},
  conversations: {},
  contentMapping: {},
  isCombinationAllowed: true,
  updateCategoryDimensions: () => { },
  brand_description: {},
  updateBrandDescription: () => { },
  brand_snippets: [],
  updateBrandSnippets: () => { },
  positive_negative_attributes: {},
  updatePositiveNegativeAttributes: () => { },
  top_products: [],
  updatedTopProducts: () => { },
  name: "",
  entity: "",
  finalTransactionId: "",
  pre_category_dimensions: [],
  rank_scores: {},
  updateEntity: () => { },
  updateName: () => { },
  updateType: () => { },
  is_launched: false,
  is_launchTransaction: false,
  updatedLaunched: () => { },
  updateEntireState: () => { },
  resetState: () => { },
  refreshState: () => { },
  brand_themes: [],
  updatedBrandThemes: () => { },
  updateCategory: () => { },
  updateMarket: () => { },
  updateLLM: () => { },
  taxonomies: {},
  updatedTaxonomies: () => { },
  resetFormState: () => { },
  abort_launch: false,
  updateAbortLaunch: () => {},
  updateLocation: () => {},
  updateOccupation: () => {},
  updateAttribute: () => {},
  updateProductPrefs: () => {},
  updatePreCategoryDimensions: () => {},
  updateRankScores: () => {},
  replaceBrandScores: () => {},
  updateDomain: () => {},
  updateTransactionId: () => {},
  updateFinalTransactionId: () => {},
  updateCustomTemplateID: () => {},
  updateTransactionStatus: () => {},
  updateFinalTransactionStatus: () => {},
  updateTransactionHistory: () => {},
  updateSelectedLLM: () => {},
  updateSelectedCompetitor: () => {},
  updateSelectedCategory: () => {},
  updateSelectedMarket: () => {},
  updateStatus: () => {},
  updateLaunchRecord: () => {},
  updateFormData:() =>{},
  updateLaunchTransaction:()=>{},
  updateSourceValidationStatus:() => { },
  updateConversations: () => {},
  deleteConversations: () =>{},
  updateContentMapping:()=>{},
  updateConversationEnabled:()=>{},
  updatePersona:()=>{},
  updateSelectedPersona:()=>{},
  updatePersonaSettings:()=>{},
  updateRbiType:()=>{},
  updateKeywords:()=>{},
  updateConversationSetting:()=>{},
  updateLaunch: () => {},
});

function discoveryReducer(state = INITIAL_STATE, action) {
  if (action.type === "UPDATE_BRAND_SCORE") {
    const { req, res, sentimentSources, isValidated } = action.payload;
    const { data, index } = res;
    const { llm, category, market,persona } = req;
    if (!state.entity) return state;
    const updatedBrandScores = [...state.brand_scores[llm][category][market][persona]];

    const rankingScores =
      state.rank_scores[llm][category][market][persona].data &&
      state.rank_scores[llm][category][market][persona].data.length > 0
        ? [...state.rank_scores[llm][category][market][persona].data]
        : [];
    const rankDataForBrandProduct = rankingScores[index];
    if (rankDataForBrandProduct && data && data?.name) {
      updatedBrandScores[index] = {
        name: data.name,
        rank: rankDataForBrandProduct.score,
        favorability: data.favorability,
        total: +rankDataForBrandProduct.score + +data.favorability,
        extras: {
          ...data.extras,
          ...state.rank_scores[llm][category][market][persona].extras,
          rank: [rankDataForBrandProduct],
          sentimentSources: sentimentSources,
          isValidated,
          rankSources:state.rank_scores[llm][category][market][persona].sources
        },
        rbiType: data?.rbiType,
      };
    } else {
      updatedBrandScores[index] = data;
    }
    const existingBrandScores = { ...state.brand_scores };
    existingBrandScores[llm][category][market][persona] = updatedBrandScores;
    return {
      ...state,
      brand_scores: existingBrandScores,
    };
  }

if (action.type === "UPDATE_SOURCES") {
  const updatedSources = { ...state.sources };
  const { res, req, showOldSourcesRecord, type } = action.payload;
  const { llm, category, market, persona } = req;
  if (!state.entity) return state;
  // Initialize required nested objects
  if (!updatedSources[llm]) updatedSources[llm] = {};
  if (!updatedSources[llm][category]) updatedSources[llm][category] = {};
  if (!updatedSources[llm][category][market] ||
      Array.isArray(updatedSources[llm][category][market])) {
    updatedSources[llm][category][market] = {};
  }
  if (!updatedSources[llm][category][market][persona]) {
    updatedSources[llm][category][market][persona] = {};
  }
  
  if (!showOldSourcesRecord) {
    // ARRAY MERGE MODE
    const newSourcesArray = Array.isArray(res) ? res : [res];
    const prevSourcesArray =
      updatedSources?.[llm]?.[category]?.[market]?.[persona]?.[type] || [];

    updatedSources[llm][category][market][persona][type] = [
      ...prevSourcesArray,
      ...newSourcesArray,
    ];
  } else {
    // OBJECT MERGE MODE
    const mergedSources = Array.isArray(res)
      ? res.reduce((acc, obj) => ({ ...acc, ...obj }), {})
      : res;

    const prevSources =
      updatedSources?.[llm]?.[category]?.[market]?.[persona]?.[type] || {};

    updatedSources[llm][category][market][persona][type] = {
      ...prevSources,
      ...mergedSources,
    };
  }

  return {
    ...state,
    sources: updatedSources,
  };
}


  if (action.type === "UPDATE_CORE_ATTRIBUTES") {
    const core_attributes = { ...state.core_attributes };
    const { category, llm, market, persona } = action.payload;
    if (!state.entity) return state;
    setNestedValue(core_attributes, [llm, category, market, persona], action.payload);
    return {
      ...state,
      core_attributes: core_attributes,
    };
  }
  if (action.type === "UPDATE_COMPETITORS") {
    console.log("UPDATE_COMPETITORS category", state.category);
    return {
      ...state,
      // competitors: uniqueArray(action.payload),
      competitors: action.payload,
    };
  }
  if (action.type === "UPDATE_CATEGORY_DIMENSIONS") {
    const category_dimensions = { ...state.category_dimensions };
    const { category, llm, market, persona } = action.payload;
    if (!state.entity) return state;
    category_dimensions[llm][category][market][persona] = action.payload;
    return {
      ...state,
      category_dimensions: category_dimensions,
    };
  }
if (action.type === "UPDATE_CONVERSATIONS") {
    const conversations = { ...state.conversations };
    const { category, llm, market, persona, data, request_id, conversation_id,conversationStatus, created_at } = action.payload;
    if (!state.entity) return state;

    if (!conversations[llm]) conversations[llm] = {};
    if (!conversations[llm][category]) conversations[llm][category] = {};
    if (!conversations[llm][category][market]) conversations[llm][category][market] = {};
    if (!conversations[llm][category][market][persona]) {
        conversations[llm][category][market][persona] = {};
    }

    const uniqueKey = String(conversation_id) || 'default';
    const existingPersonaConversations = conversations[llm][category][market][persona];

    // Remove duplicate key and 'default' key if a real conversation_id is being stored
    const filtered = Object.fromEntries(
        Object.entries(existingPersonaConversations).filter(([key]) => {
            if (key === uniqueKey) return false; // remove duplicate of current key
            if (conversation_id && key === 'default') return false; // remove stale default
            return true;
        })
    );

    conversations[llm][category][market][persona] = {
        [uniqueKey]: { data, request_id, conversation_id, created_at, conversationStatus },
        ...filtered
    };

    return {
        ...state,
        conversations,
    };
}

if (action.type === "DELETE_CONVERSATION") {
    const conversations = { ...state.conversations };
    const { category, llm, market, persona, conversation_id } = action.payload;

    if (!state.entity) return state;

    const personaConversations = conversations?.[llm]?.[category]?.[market]?.[persona];
    if (!personaConversations) return state;

    // Remove the deleted conversation key
    const updatedPersonaConversations = { ...personaConversations };
    delete updatedPersonaConversations[String(conversation_id)];

    conversations[llm][category][market][persona] = updatedPersonaConversations;

    return {
        ...state,
        conversations,
    };
}
  if (action.type === "UPDATE_CONTENTMAPPING") {
    const contentMapping = { ...state.contentMapping };
    const { category, llm } = action.payload;
    if (!state.entity) return state;
    contentMapping[llm][category] = action.payload;
    return {
      ...state,
      contentMapping,
    };
  }
  if (action.type === "UPDATE_BRAND_DESCRIPTION") {
    let updated_brand_description = { ...state.brand_description };
    let { req, res } = action.payload;
    let { category, llm } = req;
    if (!state.entity) return state;
    updated_brand_description[llm][category] = res;
    return {
      ...state,
      brand_description: updated_brand_description,
    };
  }
  if (action.type === "UPDATE_BRAND_SNIPPETS") {
    let updated_brand_snippets = { ...state.brand_snippets };
    let { req, res } = action.payload;
    let { category, llm } = req;
    if (!state.entity) return state;
    updated_brand_snippets[llm][category] = res;
    return {
      ...state,
      brand_snippets: updated_brand_snippets,
    };
  }
  // if (action.type === "UPDATE_POSITVE_NEGATIVE_ATTRIBUTES") {
  //   let updated_positive_negative_attributes = { ...state.positive_negative_attributes };
  //   let { req, res, completedTaskCount } = action.payload;
  //   let { category, llm } = req;
  //   if (!state.entity) return state;
  //   updated_positive_negative_attributes[llm][category] = res;
  //   return {
  //     ...state,
  //     positive_negative_attributes: updated_positive_negative_attributes,
  //     // status: updateStatus(state.status, TASKS.ATTRIBUTES, category, TASK_STATUS.SUCCESS)
  //     status: updateStatus(state.status.totaltasks, completedTaskCount)
  //   };
  // }
  if (action.type === "UPDATE_POSITVE_NEGATIVE_ATTRIBUTES") {
    let updated_positive_negative_attributes = {
      ...state.positive_negative_attributes,
    };
    let { req, res } = action.payload;
    let { category, llm, market, persona } = req;

    if (!state.entity) return state;

    // Ensure nested structure exists
    if (!updated_positive_negative_attributes[llm]) {
      updated_positive_negative_attributes[llm] = {};
    }
    if (!updated_positive_negative_attributes[llm][category]) {
      updated_positive_negative_attributes[llm][category] = {};
    }
    if (!updated_positive_negative_attributes[llm][category][market]) {
      updated_positive_negative_attributes[llm][category][market] = {};
    }
    if (!updated_positive_negative_attributes[llm][category][market][persona]) {
      updated_positive_negative_attributes[llm][category][market][persona] = [];
    }

    // Instead of overwrite, append new competitor object
    updated_positive_negative_attributes[llm][category][market][persona] = [
      ...updated_positive_negative_attributes[llm][category][market][persona],
      res,
    ];

    return {
      ...state,
      positive_negative_attributes: updated_positive_negative_attributes,
    };
  }

  if (action.type === "UPDATE_TOP_PRODUCTS") {
    return {
      ...state,
      top_products: action.payload,
    };
  }
  if (action.type === "UPDATE_ENTITY") {
    return {
      // ...INITIAL_STATE,
      ...state,
      entity: action.payload,
    };
  }
  if (action.type === "UPDATE_LAUNCHED") {
    return {
      ...state,
      is_launched: action.payload,
    };
  }
  if (action.type === "UPDATE_CONVERSATIONENABLED") {
    return {
      ...state,
      conversationEnabled: action.payload,
    };
  }
  if (action.type === "UPDATE_LAUNCHTRANSACTION") {
    return {
      ...state,
      is_launchTransaction: action.payload,
    };
  }
  if (action.type === "UPDATE_LAUNCHES") {
    return {
        ...state,
        auditLimits: {
          ...state.auditLimits, 
          ...action.payload,    
        },
      };
  }
  if (action.type === "UPDATE_STATE") {
    return {
      ...action.payload,
      // Preserve rbiType from current state if not explicitly provided in payload
      rbiType: action.payload.rbiType || state.rbiType,
      keywords: action.payload.keywords || state.keywords,
    };
  }
  if (action.type === "RESET_STATE") {
    resetTabName();
    return {
      ...INITIAL_STATE,
      type: state.type,
      transactionHistory: state.transactionHistory,
    };
  }

  if (action.type === "RESET_FORMSTATE") {
    return {
      ...state,
      is_resetFormState: action.payload,
    };
  }

  if (action.type === "REFRESH_STATE") {
    return {
      ...INITIAL_STATE,
      type: state.type,
      transactionHistory: state.transactionHistory,
      finalTransactionId: state.finalTransactionId,
      finalTransactionStatus: state.finalTransactionStatus,
      launchRecord: state.launchRecord,
      conversationEnabled: state.conversationEnabled,
      rbiType: state.rbiType,
      keywords: state.keywords,
      is_launched: true
    };
  }
  if (action.type === "UPDATE_BRAND_THEMES") {
    let updated_brand_themes = { ...state.brand_themes };
    let { req, res } = action.payload;
    let { category, llm } = req;
    if (!state.entity) return state;
    updated_brand_themes[llm][category] = res;
    return {
      ...state,
      brand_themes: updated_brand_themes,
    };
  }
  if (action.type === "UPDATE_NAME") {
    return {
      ...state,
      name: action.payload,
    };
  }
  if (action.type === "UPDATE_CATEGORY") {
    const category = action.payload;
    const llm = [...state.llm];
    const persona = state.persona;
    const primary_llm = state.primary_llm;
    const brand_scores = { ...state.brand_scores };
    const rank_scores = { ...state.rank_scores };
    const markets = [...state.markets];
    const taxonomies = { ...state.taxonomies };
    let market = markets[0];
    return {
      ...state,
      category: action.payload,
      brand_scores: updateScores(brand_scores, llm, category, markets,persona),
      rank_scores: updateScores(rank_scores, llm, category, markets,persona),
      brand_snippets: updateScores({ ...state.brand_snippets }, llm, category),
      brand_description: updateScores(
        { ...state.brand_description },
        llm,
        category
      ),
      brand_themes: updateScores({ ...state.brand_themes }, llm, category),
      positive_negative_attributes: updateScores(
        { ...state.attributes },
        llm,
        category,
        market,
        persona
      ),
      category_dimensions: updateScores(
        { ...state.category_dimensions },
        llm,
        category,
        market,
        persona
      ),
      conversations: updateScores(
        { ...state.conversations },
        primary_llm,
        category,
        market,
        persona
      ),
      contentMapping: updateScores(
        { ...state.contentMapping },
        llm,
        category
      ),
      core_attributes: updateScores(
        { ...state.core_attributes },
        llm,
        category,
        market,
        persona
      ),
      sources: updateScores({ ...state.sources }, llm, category, markets),
      taxonomies: updateScores(taxonomies, llm, category,market,persona),
    };
  }
  if (action.type === "UPDATE_MARKET") {
    let markets = action.payload.map((m) => m.trim());
    const llm = state.llm;
    const persona = state.persona;
    const category = state.category;
    const brand_scores = { ...state.brand_scores };
    const rank_scores = { ...state.rank_scores };
    const primary_llm = state.primary_llm;
    const taxonomies = { ...state.taxonomies };
    let market = markets[0];
    return {
      ...state,
      market: market,
      markets: markets,
      brand_scores: updateScores(brand_scores, llm, category, markets,persona),
      rank_scores: updateScores(rank_scores, llm, category, markets,persona),
      brand_snippets: updateScores({ ...state.brand_snippets }, llm, category),
      brand_description: updateScores(
        { ...state.brand_description },
        llm,
        category
      ),
      brand_themes: updateScores({ ...state.brand_themes }, llm, category),
      positive_negative_attributes: updateScores(
        { ...state.attributes },
        llm,
        category,
        market,
        persona
      ),
      category_dimensions: updateScores(
        { ...state.category_dimensions },
        llm,
        category,
        market,
        persona
      ),
      conversations: updateScores(
        { ...state.conversations },
        primary_llm,
        category,
        market,
        persona
      ),
      contentMapping: updateScores(
        { ...state.contentMapping },
        llm,
        category
      ),
      core_attributes: updateScores(
        { ...state.core_attributes },
        llm,
        category,
        market,
        persona
      ),
      sources: updateScores({ ...state.sources }, llm, category, markets),
      taxonomies: updateScores(taxonomies, llm, category,market,persona),
    };
  }
  if (action.type === "UPDATE_PERSONA") {
    const persona = action.payload;
    const llm = state.llm;
    const category = state.category;
    const brand_scores = { ...state.brand_scores };
    const rank_scores = { ...state.rank_scores };
    const markets = [...state.markets];
    return {
      ...state,
      persona: action.payload,
      brand_scores: updateScores(brand_scores, llm, category, markets,persona),
      rank_scores: updateScores(rank_scores, llm, category, markets,persona),
    };
  }

  if (action.type === "UPDATE_LLM") {
    const llm = action.payload;
    const category = state.category;
    const persona = state.persona;
    const brand_scores = { ...state.brand_scores };
    const rank_scores = { ...state.rank_scores };
    const primary_llm = state.primary_llm;
    const markets = [...state.markets];
    const taxonomies = { ...state.taxonomies };
    let market = markets[0];
    return {
      ...state,
      llm: action.payload,
      brand_scores: updateScores(brand_scores, llm, category, markets,persona),
      rank_scores: updateScores(rank_scores, llm, category, markets,persona),
      brand_snippets: updateScores({ ...state.brand_snippets }, llm, category),
      brand_description: updateScores(
        { ...state.brand_description },
        llm,
        category
      ),
      brand_themes: updateScores({ ...state.brand_themes }, llm, category),
      positive_negative_attributes: updateScores(
        { ...state.attributes },
        llm,
        category,
        market,
        persona
      ),
      category_dimensions: updateScores(
        { ...state.category_dimensions },
        llm,
        category,
        market,
        persona
      ),
      conversations: updateScores(
        { ...state.conversations },
        primary_llm,
        category,
        market,
        persona
      ),
      contentMapping: updateScores(
        { ...state.contentMapping },
        llm,
        category
      ),
      core_attributes: updateScores(
        { ...state.core_attributes },
        llm,
        category,
        market,
        persona
      ),
      sources: updateScores({ ...state.sources }, llm, category, markets),
      taxonomies: updateScores(taxonomies, llm, category,market,persona),
    };
  }

  if (action.type === "UPDATE_COMPIEITIER") {
    return { ...state, selectedCompetitor: action.payload };
  }

  if (action.type === "UPDATE_PRIMARY_LLM") {
    return {
      ...state,
      primary_llm: action.payload,
    };
  }

  if (action.type === "UPDATE_TYPE") {
    return {
      ...state,
      type: action.payload,
    };
  }
  if (action.type === "UPDATE_TAXONOMY") {
    let updated_taxonomies = { ...state.taxonomies };
    let { req, res } = action.payload;
    let { category, llm,market,persona } = req;
    if (!state.entity) return state;
    updated_taxonomies[llm][category][market][persona] = res;
    return {
      ...state,
      taxonomies: updated_taxonomies,
    };
  }
  if (action.type === "UPDATE_ID") {
    return {
      ...state,
      id: action.payload,
    };
  }
  if (action.type === "UPDATE_TRANSACTIONID") {
    return {
      ...state,
      transactionId: action.payload,
    };
  }
  if (action.type === "UPDATE_FINALTRANSACTIONID") {
    return {
      ...state,
      finalTransactionId: action.payload,
    };
  }
  if (action.type === "UPDATE_CUSTOMTEMPLATEID") {
    return {
      ...state,
      customTemplateID: action.payload,
    };
  }
  if (action.type === "UPDATE_RBITYPE") {
    return {
      ...state,
      rbiType: action.payload,
    };
  }
   if (action.type === "UPDATE_COVERSATIONSETTING") {
    return {
      ...state,
      conversationSettings: action.payload,
    };
  }
  if (action.type === "UPDATE_KEYWORDS") {
    return {
      ...state,
      keywords: action.payload,
    };
  }
  if (action.type === "UPDATE_STATUS") {
    let { totalCount, completedTaskCount } = action.payload;
    totalCount = totalCount ?? state.status?.totalCount ?? 0;
    completedTaskCount =
      completedTaskCount ?? state.status?.completedTaskCount ?? 0;
    return {
      ...state,
      status: updateCount(totalCount, completedTaskCount),
    };
  }
  if (action.type === "UPDATE_TRANSACTIONSTATUS") {
    return {
      ...state,
      transactionStatus: action.payload,
    };
  }

  if (action.type === "UPDATE_SOURCEVALIDATIONSTATUS") {
    return {
      ...state,
      sourceValidationStatus: action.payload,
    };
  }

  if (action.type === "UPDATE_FINALTRANSACTIONSTATUS") {
    return {
      ...state,
      finalTransactionStatus: action.payload,
    };
  }

  if (action.type === "UPDATE_TRANSACTIONHISTORY") {
    if (action.payload === null || action.payload.clear) {
      return { ...state, transactionHistory: [] };
    }
    if (action.payload.replace) {
      return { ...state, transactionHistory: action.payload.data || [] };
    }
return {
  ...state,
  transactionHistory: action.payload || [], 
};
  }

  if (action.type === "UPDATE_LAUNCHRECORD") {
    const payload = action.payload;

      /* DELETE CASE (ARRAY PAYLOAD) */
  if (Array.isArray(payload)) {
    return {
      ...state,
      launchRecord: payload
    };
  }

    // If no existing launchRecord, just start with payload
    if (!state.launchRecord || state.launchRecord.length === 0) {
      return {
        ...state,
        launchRecord: [payload],
      };
    }

    // If both transactionId and name exist → append payload
    if (payload.transactionId && payload.name) {
      return {
        ...state,
        launchRecord: [...state.launchRecord, payload],
      };
    }

    // If only transactionId → update last object
    if (payload.transactionId) {
      const updatedLaunchRecord = [...state.launchRecord];
      const lastIndex = updatedLaunchRecord.length - 1;

      updatedLaunchRecord[lastIndex] = {
        ...updatedLaunchRecord[lastIndex],
        ...payload,
      };

      return {
        ...state,
        launchRecord: updatedLaunchRecord,
      };
    }

    // Default → append payload
    return {
      ...state,
      launchRecord: [...state.launchRecord, payload],
    };
  }

  if (action.type === "UPDATE_SELECTEDLLM") {
    return {
      ...state,
      selectedLLM: action.payload,
    };
  }
if (action.type === "UPDATE_SELECTEDCATRGORY") {
  const { persona, market, category,isCombinationAllowed } = resolveSelectionRules(
    state,
    null,
    null,
    action.payload
  );

  return {
    ...state,
    selectedPersona: persona,
    selectedMarket: market,
    selectedCategory: category,
    isCombinationAllowed
  };
}

  if (action.type === "UPDATE_PERSONASETTING") {
    return {
      ...state,
      personaSettings: action.payload,
    };
  }
if (action.type === "UPDATE_SELECTEDMARKET") {
  const { persona, market, category,isCombinationAllowed } = resolveSelectionRules(
    state,
    null,
    action.payload,
    null
  );

  return {
    ...state,
    selectedPersona: persona,
    selectedMarket: market,
    selectedCategory: category,
    isCombinationAllowed
  };
}

  if (action.type === "UPDATE_SELECTEDPERSONA") {
  const { persona, market, category,isCombinationAllowed } = resolveSelectionRules(
    state,
    action.payload,
    null,
    null
  );

  return {
    ...state,
    selectedPersona: persona,
    selectedMarket: market,
    selectedCategory: category,
    isCombinationAllowed
  };
}

  if (action.type === "ABORT_LAUNCH") {
    return {
      ...state,
      abort_launch: action.payload,
    };
  }

  if (action.type === "UPDATE_DOMAIN") {
    return {
      ...state,
      domain: action.payload,
    };
  }

  if (action.type === "UPDATE_LOCATION") {
    return {
      ...state,
      location: action.payload,
    };
  }
  if (action.type === "UPDATE_OCCUPATION") {
    return {
      ...state,
      occupation: action.payload,
    };
  }
  if (action.type === "UPDATE_ATTRIBUTE") {
    return {
      ...state,
      attributes: action.payload,
    };
  }
  if (action.type === "UPDATE_CATEGORY_TAXONOMIES") {
    let updated_category_taxonomies = { ...state.category_taxonomies };
    let { req, res } = action.payload;
    if (!req) return state;
    let { category } = req;
    updated_category_taxonomies[category] = res;
    return {
      ...state,
      category_taxonomies: updated_category_taxonomies,
    };
  }
  if (action.type === "UPDATE_PRODUCT_PREFS") {
    return {
      ...state,
      product_prefs: action.payload,
    };
  }
  if (action.type === "UPDATE_PRE_CATEGORY_DIMENSIONS") {
    return {
      ...state,
      pre_category_dimensions: action.payload,
    };
  }
  if (action.type === "UPDATE_LOADING") {
    return {
      ...state,
      loading: action.payload,
    };
  }
  if (action.type === "UPDATE_FORMDATA") {
    return {
      ...state,
      formData: action.payload,
    };
  }
  if (action.type === "UPDATE_RANK_SCORES") {
    const { req, res } = action.payload;
    const { data, extras, sources,baseResponses } = res;
    const { llm, category, market,persona } = req;
    if (!state.entity) return state;
    let updatedBrandScores = [...state.brand_scores[llm][category][market][persona]];

    Array.isArray(data) &&
      data.forEach((item, index) => {
        let brandIndexItem = updatedBrandScores[index];
        if (brandIndexItem && brandIndexItem.favorability) {
          updatedBrandScores[index] = {
            name: item.name,
            rank: item.score,
            favorability: brandIndexItem.favorability,
            total: +item.score + +brandIndexItem.favorability,
            extras: {
              ...extras,
              ...brandIndexItem.extras,
              rank: [item],
              sentimentSources: brandIndexItem.sentimentSources,
              isValidated: brandIndexItem.isValidated,
              rankSources: sources,
            },
            rbiType: brandIndexItem?.rbiType,
          };
        } else {
          updatedBrandScores[index] = {
            ...updatedBrandScores[index],
            name: item.name,
            rank: item.score,
            extras: {
              ...extras,
              rank: [item],
            },
          };
        }
      });

    const existingBrandScores = { ...state.brand_scores };
    existingBrandScores[llm][category][market][persona] = updatedBrandScores;

    const updatedRankScores = { ...state.rank_scores };
    updatedRankScores[llm][category][market][persona] = action.payload.res;
    return {
      ...state,
      rank_scores: updatedRankScores,
      brand_scores: existingBrandScores,
    };
  }
  if (action.type === "REPLACE_BRAND_SCORES") {
    return {
      ...state,
      brand_scores: action.payload,
    };
  }

  /*action for button  click of report checkbox*/
  if (action.type === "Check_Report_Checkbox") {
    return {
      ...state,
      showReportCheckbox: action.payload,
      showAnimation: false,
    };
  }

  if (action.type === "hideAnimation") {
    return {
      ...state,
      showAnimation: false,
    };
  }
  if (action.type === "showAnimation") {
    return {
      ...state,
      showAnimation: true,
    };
  }

  if (action.type === "cancel_selection") {
    return {
      ...state,
      showReportCheckbox: false,
      selectAll: false,
      selectedSection: {},
    };
  }

  if (action.type === "setMetricObject") {
    return {
      ...state,
      metricSettingObject: action.payload
    };
  }

  /*handle action of user click on checkbox  of component so need to check is key exist and updates the values*/
  /*for tabName what happen we need tab name in each card so we add as it is but in case of llmName what happening some section in ui don't depend on llm like market
   summary, brand summary for such case in filter we pass llm as null so it is not use in filter so that why i checking is llm key present filter then check its value
   llm key if null then set empty value otherwise set llmName*/
  if (action.type === "click_component_checkbox") {
    const { uniqueKey, filter, checked } = action.payload;
    if (checked) {
      delete state.selectedSection?.[uniqueKey];
    } else {
      let navTab = filter?.tab ?? "";
      const LLMName = OVERVIEW_LLM_LABELS?.[state.selectedLLM] ?? state.selectedLLM;
      const primaryLLMName = OVERVIEW_LLM_LABELS?.[state.primary_llm] ?? state.selectedLLM;
      /*we alway not pass llm in filter  only  we pass when we want to set llm null or some specific value so we first check is llm  pass in filter if not then assign
      llm name directly but llm is pass in filter then check its value if value is null then llmName is also null*/
      let llmName = !filter.hasOwnProperty("llm")
        ? LLMName
        : filter.llm
          ? LLMName
          : null;

      let TabName = tabName[navTab];

      if (filter.tab === "nev_brandoverview")
        state.selectedSection[uniqueKey] = {
          llm: state.selectedLLM,
          category: state.selectedCategory,
          market: state.selectedMarket,
          persona:state.selectedPersona,
          TabName,
          llmName,
          ...filter,
        };
      else if (filter.tab === "nev_competitiveoverview") {
        state.selectedSection[uniqueKey] = {
          llm: state.selectedLLM,
          category: state.selectedCategory,
          persona:state.selectedPersona,
          market:state.selectedMarket,
          TabName,
          llmName,
          ...filter,
        };
      } else if (filter.tab === "nev_marketoverview")
        state.selectedSection[uniqueKey] = {
          llm: state.selectedLLM,
          category: state.selectedCategory,
          market: state.selectedMarket,
          persona:state.selectedPersona,
          TabName,
          llmName,
          ...filter,
        };
        else if (filter.tab === "nev_personaoverview")
        state.selectedSection[uniqueKey] = {
          llm: state.selectedLLM,
          category: state.selectedCategory,
          market: state.selectedMarket,
          persona:state.selectedPersona,
          TabName,
          llmName,
          ...filter,
        } 
        else if (filter.tab === "nev_conversation")
        state.selectedSection[uniqueKey] = {
          llm: state.primary_llm,
          category: state.selectedCategory,
          market: state.market,
          TabName,
          primaryLLMName,
          ...filter,
        } 
      else {
        state.selectedSection[uniqueKey] = {
          llm: state.selectedLLM,
          llmName,
          TabName,
          ...filter,
          persona:state.selectedPersona,
          isCategoryTab: true
        };
      }
    }
    return {
      ...state,
    };
  }
  if (action.type === "click_select_all") {
    return {
      ...state,
      selectedSection: action.payload,
      selectAll: !state.selectAll,
    };
  }

if (action.type === "toggle_dashboard_selection") {
  const { componentKeys,clear } = action.payload;
  if (clear) {
      return { ...state, selectedSection: {} };
  }

  const newSelectedSection = { ...state.selectedSection };
  // Check if all components are currently selected
  const allSelected = componentKeys.every(
    (comp) => newSelectedSection[comp.uniqueKey]
  );
  // If all selected, remove them; otherwise add them
  if (allSelected) {
    // Deselect all components in this dashboard
    componentKeys.forEach((comp) => {
      delete newSelectedSection[comp.uniqueKey];
    });
  } else {
    // Select all components in this dashboard
    componentKeys.forEach((comp) => {
      const { uniqueKey, filters } = comp;
      const navTab = filters?.tab ?? "";
      const LLMName = OVERVIEW_LLM_LABELS?.[filters.llm] ?? filters.llm;
      const primaryLLMName = OVERVIEW_LLM_LABELS?.[state.primary_llm] ?? state.primary_llm;
      
      let llmName = !filters.hasOwnProperty("llm")
        ? LLMName
        : filters.llm
          ? LLMName
          : null;

      let TabName = tabName[navTab];


      if (filters.tab === "nev_brandoverview") {
        newSelectedSection[uniqueKey] = {
          llm: filters.llm,
          category: filters.category,
          market: filters.market,
          persona: filters.persona,
          TabName,
          llmName,
          ...filters,
        };
      } else if (filters.tab === "nev_competitiveoverview") {
        newSelectedSection[uniqueKey] = {
          llm: filters.llm,
          category: filters.category,
          persona: filters.persona,
          market: filters.market,
          TabName,
          llmName,
          ...filters,
        };
      } else if (filters.tab === "nev_marketoverview") {
        newSelectedSection[uniqueKey] = {
          llm: filters.llm ,
          category: filters.category,
          market: filters.market,
          persona: filters.persona,
          TabName,
          llmName,
          ...filters,
        };
      } else if (filters.tab === "nev_personaoverview") {
        newSelectedSection[uniqueKey] = {
          llm: filters.llm,
          category: filters.category,
          market: filters.market,
          persona: filters.persona,
          TabName,
          llmName,
          ...filters,
        };
      } else if (filters.tab === "nev_conversation") {
        newSelectedSection[uniqueKey] = {
          llm: state.primary_llm,
          category: filters.category,
          market: state.market,
          TabName,
          primaryLLMName,
          ...filters,
        };
      } else {
        // Category tabs
        newSelectedSection[uniqueKey] = {
          llm: filters.llm,
          llmName,
          TabName,
          ...filters,
          persona: filters.persona ?? state.selectedPersona,
          isCategoryTab: true,
        };
      }
    });
  }
  
  return {
    ...state,
    selectedSection: newSelectedSection,
  };
}

  return state;
}

export default function DiscoveryProvider({ children }) {
  const [state, dispatch] = useReducer(discoveryReducer, INITIAL_STATE);
  const [page, setPage] = useState(1);

  function updateLoading(payload) {
    dispatch({ type: "UPDATE_LOADING", payload });
  }

  function updateFormData(payload) {
    dispatch({ type: "UPDATE_FORMDATA", payload });
  }

  function updateId(payload) {
    dispatch({ type: "UPDATE_ID", payload });
  }

  function updateBrandScores(payload) {
    dispatch({ type: "UPDATE_BRAND_SCORE", payload });
  }

  function updateSources(payload) {
    dispatch({ type: "UPDATE_SOURCES", payload });
  }

  function updateCoreAttributes(payload) {
    dispatch({ type: "UPDATE_CORE_ATTRIBUTES", payload });
  }

  function updatedCompetitors(payload) {
    dispatch({ type: "UPDATE_COMPETITORS", payload });
  }

  function updateCategoryDimensions(payload) {
    dispatch({ type: "UPDATE_CATEGORY_DIMENSIONS", payload });
  }

  function updateConversations(payload) {
    dispatch({ type: "UPDATE_CONVERSATIONS", payload });
  }

   function deleteConversations(payload) {
    dispatch({ type: "DELETE_CONVERSATION", payload });
  }

  function updateContentMapping(payload) {
    dispatch({ type: "UPDATE_CONTENTMAPPING", payload });
  }

  function updateBrandDescription(payload) {
    dispatch({ type: "UPDATE_BRAND_DESCRIPTION", payload });
  }

  function updateConversationEnabled(payload) {
    dispatch({ type: "UPDATE_CONVERSATIONENABLED", payload });
  }

  function updateBrandSnippets(payload) {
    dispatch({ type: "UPDATE_BRAND_SNIPPETS", payload });
  }

  function updatePositiveNegativeAttributes(payload) {
    dispatch({ type: "UPDATE_POSITVE_NEGATIVE_ATTRIBUTES", payload });
  }

  function updatedTopProducts(payload) {
    dispatch({ type: "UPDATE_TOP_PRODUCTS", payload });
  }

  function updateStatus(payload) {
    dispatch({ type: "UPDATE_STATUS", payload });
  }

  function updateEntity(payload) {
    dispatch({ type: "UPDATE_ENTITY", payload });
  }

  function updatedLaunched(payload) {
    dispatch({ type: "UPDATE_LAUNCHED", payload });
  }

  function updateLaunchTransaction(payload) {
    dispatch({ type: "UPDATE_LAUNCHTRANSACTION", payload });
  }

  function updateLaunch(payload) {
    dispatch({ type: "UPDATE_LAUNCHES", payload });
  }

  function updateEntireState(payload) {
    dispatch({ type: "UPDATE_STATE", payload });
  }

  function updateTransactionId(payload) {
    dispatch({ type: "UPDATE_TRANSACTIONID", payload });
  }

  function updateFinalTransactionId(payload) {
    dispatch({ type: "UPDATE_FINALTRANSACTIONID", payload });
  }

  function updateCustomTemplateID(payload) {
    dispatch({ type: "UPDATE_CUSTOMTEMPLATEID", payload });
  }

  function updateRbiType(payload) {
    dispatch({ type: "UPDATE_RBITYPE", payload });
  }

  function updateConversationSetting(payload) {
    dispatch({ type: "UPDATE_COVERSATIONSETTING", payload });
  }

  function updateKeywords(payload) {
    dispatch({ type: "UPDATE_KEYWORDS", payload });
  }

  function updateTransactionStatus(payload) {
    dispatch({ type: "UPDATE_TRANSACTIONSTATUS", payload });
  }

  function updateSourceValidationStatus(payload) {
    dispatch({ type: "UPDATE_SOURCEVALIDATIONSTATUS", payload });
  }

  function updateFinalTransactionStatus(payload) {
    dispatch({ type: "UPDATE_FINALTRANSACTIONSTATUS", payload });
  }

  function updateTransactionHistory(payload) {
    dispatch({ type: "UPDATE_TRANSACTIONHISTORY", payload });
  }

  function updateLaunchRecord(payload) {
    dispatch({ type: "UPDATE_LAUNCHRECORD", payload });
  }

  function updateSelectedLLM(payload) {
    dispatch({ type: "UPDATE_SELECTEDLLM", payload });
  }
  function updateSelectedCategory(payload) {
    dispatch({ type: "UPDATE_SELECTEDCATRGORY", payload });
  }

  function updateSelectedPersona(payload) {
    dispatch({ type: "UPDATE_SELECTEDPERSONA", payload });
  }

  function updatePersonaSettings(payload) {
    dispatch({ type: "UPDATE_PERSONASETTING", payload });
  }

  function updateSelectedMarket(payload) {
    dispatch({ type: "UPDATE_SELECTEDMARKET", payload });
  }

  function updateSelectedCompetitor(payload) {
    dispatch({ type: "UPDATE_COMPIEITIER", payload });
  }
  function resetState() {
    dispatch({ type: "RESET_STATE" });
  }
  function refreshState() {
    dispatch({ type: "REFRESH_STATE" });
  }

  function resetFormState(payload) {
    dispatch({ type: "RESET_FORMSTATE", payload });
  }

  function updatedBrandThemes(payload) {
    dispatch({ type: "UPDATE_BRAND_THEMES", payload });
  }

  function updateName(payload) {
    dispatch({ type: "UPDATE_NAME", payload });
  }

  function updateCategory(payload) {
    dispatch({ type: "UPDATE_CATEGORY", payload });
  }

  function updateMarket(payload) {
    dispatch({ type: "UPDATE_MARKET", payload });
  }

  function updateLLM(payload) {
    dispatch({ type: "UPDATE_LLM", payload });
  }

  function updatePersona(payload) {
    dispatch({ type: "UPDATE_PERSONA", payload });
  }

  function updatePrimaryLLM(payload) {
    dispatch({ type: "UPDATE_PRIMARY_LLM", payload });
  }

  function updateType(payload) {
    dispatch({ type: "UPDATE_TYPE", payload });
  }

  function updatedTaxonomies(payload) {
    dispatch({ type: "UPDATE_TAXONOMY", payload });
  }

  function updateLocation(payload) {
    dispatch({ type: "UPDATE_LOCATION", payload });
  }

  function updateOccupation(payload) {
    dispatch({ type: "UPDATE_OCCUPATION", payload });
  }

  function updateAttribute(payload) {
    dispatch({ type: "UPDATE_ATTRIBUTE", payload });
  }

  function updateRankScores(payload) {
    dispatch({ type: "UPDATE_RANK_SCORES", payload });
  }

  function updateDomain(payload) {
    dispatch({ type: "UPDATE_DOMAIN", payload });
  }

  function updateAbortLaunch(payload) {
    dispatch({ type: "ABORT_LAUNCH", payload });

    setTimeout(() => {
      updateLoading(false);
      updatedLaunched(false);
      resetState();
    }, 100);
  }

  function replaceBrandScores(payload) {
    dispatch({ type: "REPLACE_BRAND_SCORES", payload });
  }

  function updateCategoryTaxonomies(payload) {
    dispatch({ type: "UPDATE_CATEGORY_TAXONOMIES", payload });
  }

  function updateProductPrefs(payload) {
    dispatch({ type: "UPDATE_PRODUCT_PREFS", payload });
  }

  function updatePreCategoryDimensions(payload) {
    dispatch({ type: "UPDATE_PRE_CATEGORY_DIMENSIONS", payload });
  }

  function initialize(payload) {
    dispatch({ type: "INITIALIZE", payload });
  }

  /*handle  report checkbox  button click*/
  function showReportCheckbox(payload) {
    dispatch({ type: "Check_Report_Checkbox", payload });
  }

  /*handle  component checkbox  click*/
  function clickComponentCheckbox(payload) {
    dispatch({ type: "click_component_checkbox", payload });
  }

  function toggleDashboardSelection(payload) {
  dispatch({ 
    type: "toggle_dashboard_selection", 
    payload
  });
}


  /*handle  component checkbox  click*/
  function clickSelectAll(payload) {
    dispatch({ type: "click_select_all", payload });
  }

  /*handle  component checkbox  click*/
  function showAnimation(payload) {
    dispatch({ type: "showAnimation", payload });
  }

  /*handle  component checkbox  click*/
  function hideAnimation(payload) {
    dispatch({ type: "hideAnimation", payload });
  }

  function cancelSelection(payload) {
    dispatch({ type: "cancel_selection", payload });
  }

  function setMetricSettingObj(payload) {
    dispatch({ type: "setMetricObject", payload });
  }

  function setMetricSettingObj(payload) {
    dispatch({ type: "setMetricObject", payload });
  }

  // Determine if current brand_scores correspond to a Propensity To Buy RBI type
  function isPropensityToBuy({ llm, category, market, persona } = {}) {
    const llmKey = llm ?? state.selectedLLM ?? state.primary_llm ?? state.llm?.[0];
    const categoryKey = category ?? state.selectedCategory ?? state.category?.[0];
    const marketKey = market ?? state.selectedMarket ?? state.market ?? state.markets?.[0];
    const personaKey = persona ?? state.selectedPersona ?? state.persona?.[0]?.title ?? state.persona?.[0];

    try {
      const arr = state.brand_scores?.[llmKey]?.[categoryKey]?.[marketKey]?.[personaKey];
      if (!Array.isArray(arr) || arr.length === 0) return false;
      return arr.some(
        (item) => item?.rbiType === "propensityToBuy" || item?.rbiType === "propensity_to_buy"
      );
    } catch (err) {
      return false;
    }
  }

  // Map metric data keys when working with propensity RBI type
function mapMetricKey(metric, opts = {}) {
  if (isPropensityToBuy(opts)) {
    if (metric === ENTITY_LABELS.rank?.toLowerCase()) {
      return "propensity-visibility";
    }
    if (metric === "sentiment") {
      return "propensity-to-buy";
    }
  }
  return metric;
}


  // Map display titles/labels for UI when working with propensity RBI type
function mapMetricTitle(title, opts = {}) {
  if (!isPropensityToBuy(opts)) return title;
  if (!title || typeof title !== "string") return title;

  const rankLabel = ENTITY_LABELS.rank?.toLowerCase();

  return title
    .replace(/sentiment/gi, "Purchase Intent")
    .replace(
      new RegExp(rankLabel, "gi"),
      `Purchase Intent ${ENTITY_LABELS.rank}`
    );
}

  return (
    <DiscoveryContext.Provider
      value={{
        state: state,
        updateId,
        page,
        setPage,
        location: state.location,
        attributes: state.attributes,
        occupation: state.occupation,
        loading: state.loading,
        updateLoading: updateLoading,
        rank_scores: state.rank_scores,
        product_prefs: state.product_prefs,
        brand_scores: state.brand_scores,
        sources: state.sources,
        core_attributes: state.core_attributes,
        competitors: state.competitors,
        category_dimensions: state.category_dimensions,
        conversations: state.conversations,
        contentMapping: state.contentMapping,
        brand_description: state.brand_description,
        brand_snippets: state.brand_snippets,
        brand_themes: state.brand_themes,
        positive_negative_attributes: state.positive_negative_attributes,
        top_products: state.top_products,
        entity: state.entity,
        name: state.name,
        category: state.category,
        persona:state.persona,
        market: state.market,
        llm: state.llm,
        primary_llm: state.primary_llm,
        is_launched: state.is_launched,
        is_launchTransaction: state.is_launchTransaction,
        taxonomies: state.taxonomies,
        type: state.type,
        pre_category_dimensions: state.pre_category_dimensions,
        abort_launch: state.abort_launch,
        category_taxonomies: state.category_taxonomies,
        status: state.status,
        markets: state.markets,
        domain: state.domain,
        formData: state.formData,
        is_resetFormState: state.is_resetFormState,
        customTemplateID: state.customTemplateID,
        conversationEnabled: state.conversationEnabled,
        auditLimits:state.auditLimits,
        conversationSettings:state.conversationSettings,
        updateBrandScores,
        updateSources,
        updateCoreAttributes,
        updatedCompetitors,
        updateCategoryDimensions,
        updateBrandDescription,
        updateBrandSnippets,
        updatePositiveNegativeAttributes,
        updatedTopProducts,
        updateEntity,
        updateName,
        updatedLaunched,
        updateEntireState,
        resetState,
        resetFormState,
        refreshState,
        updatedBrandThemes,
        updateCategory,
        updateMarket,
        updateLLM,
        updatePrimaryLLM,
        updateType,
        updatedTaxonomies,
        updateAbortLaunch,
        updateAttribute,
        updateLocation,
        updateOccupation,
        updateCategoryTaxonomies,
        updateProductPrefs,
        updatePreCategoryDimensions,
        updateRankScores,
        updateDomain,
        replaceBrandScores,
        initialize,
        updateTransactionId,
        updateFinalTransactionId,
        updateCustomTemplateID,
        updateRbiType,
        updateKeywords,
        updateTransactionStatus,
        updateFinalTransactionStatus,
        updateTransactionHistory,
        updateSelectedLLM,
        updateSelectedCategory,
        updateSelectedMarket,
        showReportCheckbox,
        clickComponentCheckbox,
        toggleDashboardSelection,
        updateSelectedCompetitor,
        cancelSelection,
        showAnimation,
        hideAnimation,
        clickSelectAll,
        updateStatus,
        updateLaunchRecord,
        updateFormData,
        updateLaunchTransaction,
        setMetricSettingObj,
        updateSourceValidationStatus,
        updateConversations,
        deleteConversations,
        updateContentMapping,
        isPropensityToBuy,
        mapMetricKey,
        mapMetricTitle,
        updateConversationEnabled,
        updatePersona,
        updateSelectedPersona,
        updatePersonaSettings,
        updateConversationSetting,
        updateLaunch
      }}
    >
      {children}
    </DiscoveryContext.Provider>
  );
}

/*In some case  uniqueKey is only depend on only one thing or factor like in competitor overview the competitor summary only depend
  on selectedCategory  no other on selectedLLM ,SelectedCompetitor, selectedMarket so no meaning of includes those in uniqueKey*/
export function useIsSelectedSection({
  id,
  tab = "nev_brandoverview",
  uniqueKey,
}) {
  const discoveryCtx = useContext(DiscoveryContext);
  const selectedLLM = discoveryCtx.state.selectedLLM;
  const selectedCategory = discoveryCtx.state.selectedCategory;
  const selectedMarket = discoveryCtx.state.selectedMarket;
  const selectedPersona = discoveryCtx.state.selectedPersona;

  if (!uniqueKey) {
    if (tab === "nev_brandoverview") {
      uniqueKey = `nev_brandoverview_${id}_${selectedLLM ?? ""}_${selectedCategory ?? ""
        }_${selectedMarket ?? ""}_${selectedPersona ?? ""}`;
    } else if (tab === "nev_competitiveoverview") {
      uniqueKey = `nev_competitiveoverview_${id}_${selectedLLM ?? ""}_${selectedCategory ?? ""
        }_${selectedPersona ?? ""}`;
    } else if (tab === "nev_marketoverview") {
      uniqueKey = `nev_marketoverview_${id}_${selectedLLM ?? ""}_${selectedCategory ?? ""
        }_${selectedMarket ?? ""}_${selectedPersona ?? ""}`;
    }  else if (tab === "nev_personaoverview") {
      uniqueKey = `nev_personaoverview_${id}_${selectedLLM ?? ""}_${selectedCategory ?? ""
        }_${selectedMarket ?? ""}_${selectedPersona ?? ""}`;
    }
  }
  const selectedContextRef = discoveryCtx.state?.selectedSection ?? {};

  return { checked: selectedContextRef?.[uniqueKey] ? true : false, uniqueKey };
}
